import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import CustomersPage from "@/pages/CustomersPage";
import Customer360Page from "@/pages/Customer360Page";
import LeadsPage from "@/pages/LeadsPage";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import TasksPage from "@/pages/TasksPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import ReportsPage from "@/pages/ReportsPage";
import AdminPage from "@/pages/AdminPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import PublicCvUploadPage from "@/pages/PublicCvUploadPage";
import PipelinesPage from "@/pages/PipelinesPage";
import CallsPage from "@/pages/CallsPage";
import CalendarPage from "@/pages/CalendarPage";
import ProcessPage from "@/pages/ProcessPage";
import ProcessCaseDetailPage from "@/pages/ProcessCaseDetailPage";
import CaseWorkflowSetupPage from "@/pages/CaseWorkflowSetupPage";
import SupportPage from "@/pages/SupportPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import FinancePage from "@/pages/FinancePage";
import MyCoachingPage from "@/pages/MyCoachingPage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import KnowledgeViewerPage from "@/pages/KnowledgeViewerPage";
import WebinarsPage from "@/pages/WebinarsPage";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
};

// Same auth gate as Protected, but WITHOUT AppShell's sidebar/header chrome
// — the Knowledge Viewer is meant to open in its own focused tab (D1/D2),
// not a full app navigation context. Still fully authenticated; the
// backend independently re-checks document visibility either way.
const ProtectedBare = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/book/:token" element={<PublicBookingPage />} />
      {/* 2026-09 Step 2 — deliberately a SEPARATE public route/page from
          /book/:token: this is NOT part of Manage Booking (different
          token collection, different purpose, no reschedule/cancel here). */}
      <Route path="/upload/:token" element={<PublicCvUploadPage />} />
      <Route path="/" element={<Protected><HomePage /></Protected>} />
      <Route path="/pipelines" element={<Protected><PipelinesPage /></Protected>} />
      <Route path="/customers" element={<Protected><CustomersPage /></Protected>} />
      <Route path="/customers/:contactId" element={<Protected><Customer360Page /></Protected>} />
      <Route path="/calls" element={<Protected><CallsPage /></Protected>} />
      <Route path="/leads" element={<Protected><LeadsPage /></Protected>} />
      <Route path="/opportunities" element={<Protected><OpportunitiesPage /></Protected>} />
      <Route path="/tasks" element={<Protected><TasksPage /></Protected>} />
      <Route path="/consultations" element={<Protected><AppointmentsPage /></Protected>} />
      <Route path="/appointments" element={<Navigate to="/consultations" replace />} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/process" element={<Protected><ProcessPage /></Protected>} />
      {/* Workstream C — the Case detail is now a REAL route, not a modal,
          mirroring the /customers/:contactId precedent above. The previous
          query-string contract (?queue=&case=) still resolves: the list
          page redirects a ?case=CS-… URL here, so existing links and
          anything already deep-linking into the old modal keep working. */}
      {/* 2026-09 — Existing Case Workflow Setup (admin-only review screen,
          reached from the Process / Cases banner). A static segment, so it
          always wins over /process/:caseId. */}
      <Route path="/process/workflow-setup" element={<Protected><CaseWorkflowSetupPage /></Protected>} />
      <Route path="/process/:caseId" element={<Protected><ProcessCaseDetailPage /></Protected>} />
      <Route path="/support" element={<Protected><SupportPage /></Protected>} />
      <Route path="/approvals" element={<Protected><ApprovalsPage /></Protected>} />
      <Route path="/finance" element={<Protected><FinancePage /></Protected>} />
      {/* 2026-09 — the operational home for webinar campaigns (global
          webinar config stays in Administration -> Webinar). */}
      <Route path="/webinars" element={<Protected><WebinarsPage /></Protected>} />
      <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
      <Route path="/my-coaching" element={<Protected><MyCoachingPage /></Protected>} />
      <Route path="/knowledge-base" element={<Protected><KnowledgeBasePage /></Protected>} />
      <Route path="/knowledge-base/view/:documentId" element={<ProtectedBare><KnowledgeViewerPage /></ProtectedBare>} />
      <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors={false} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
